import { Button, Card, Flex, Form, Select, Space, Typography } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import CodeFrameSelect from '../../../components/CodeFrameSelect';
import {
  QuestionProcessingType,
  QuestionTypeValues,
  SurveyProcessingType,
} from '../../../constants';
import useOrganizationType from '../../../hooks/useOrganizationType';
import { surveyDetailsType } from '../../../types';

const { Title, Paragraph, Text } = Typography;

const StyledParagraph = styled(Paragraph)`
  text-indent: 8px;
`;

const StyledTitle = styled(Title)`
  margin-top: 0;
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 8px;
`;

function CodingGuidance({ surveyData, prev, createSurveyLoading, finishUpload, setSurveyData }) {
  const [form] = Form.useForm();
  const openEndedQuestions = surveyData.questions.filter(
    (q) => q.columnType === QuestionTypeValues.HAS_THEMES,
  );

  const { isProAccount, isHybridAccount } = useOrganizationType();
  const processingType = surveyData?.surveyDetails?.processing_type;
  const isProProcessing =
    isProAccount || (isHybridAccount && processingType === SurveyProcessingType.PRO);

  const onFinish = () => {
    finishUpload();
  };

  const onMethodChange = (index, value) => {
    setSurveyData((prevData) => {
      const updatedQuestions = prevData.questions.map((q) =>
        q.index === index ? { ...q, processing_type: value } : q,
      );
      return { ...prevData, questions: updatedQuestions };
    });
  };

  const onQuestionSelect = (index, value) => {
    setSurveyData((prevData) => {
      const updatedQuestions = prevData.questions.map((q) =>
        q.index === index ? { ...q, import_code_frame_id: value } : q,
      );
      return { ...prevData, questions: updatedQuestions };
    });
  };

  return (
    <Form
      onFinish={onFinish}
      autoComplete="off"
      requiredMark={false}
      form={form}
      colon={false}
      layout="vertical"
    >
      <Title level={3}>Select code frame method</Title>
      <Paragraph type="secondary">
        Choose how you want to create the code frame for each question:
      </Paragraph>
      {isProProcessing ? (
        <>
          <StyledParagraph type="secondary">
            <Text type="secondary" strong>
              • Reapply from past Fathom project:
            </Text>{' '}
            Start with a code frame created for a previous Fathom project, then refine and apply it
            to your data.
          </StyledParagraph>
          <StyledParagraph type="secondary">
            <Text type="secondary" strong>
              • Enter your own:
            </Text>{' '}
            skip theme suggestions and manually enter any preexisting code frame for Fathom to apply
            to your data.
          </StyledParagraph>
          <StyledParagraph type="secondary">
            <Text type="secondary" strong>
              • Start with suggested themes:
            </Text>{' '}
            Fathom will generate a customized code frame based on your goals, ready for you to
            refine and confirm.
          </StyledParagraph>
        </>
      ) : (
        <>
          <StyledParagraph type="secondary">
            <Text type="secondary" strong>
              • Reapply from past Fathom project:
            </Text>{' '}
            reuse a code frame created with Fathom during a previous project or wave of research.
          </StyledParagraph>
          <StyledParagraph type="secondary">
            <Text type="secondary" strong>
              • Provide your own:
            </Text>{' '}
            share a code frame you&apos;ve used outside Fathom, and we&apos;ll implement it for you.
          </StyledParagraph>
          <StyledParagraph type="secondary">
            <Text type="secondary" strong>
              • Create from scratch to meet your goals:
            </Text>{' '}
            Fathom will build a custom code frame tailored to your goals and research questions.
          </StyledParagraph>
        </>
      )}
      <Flex vertical gap={16}>
        {openEndedQuestions.map((q) => (
          <Card size="small" key={q.index}>
            <Flex vertical gap={8}>
              <StyledTitle level={5}>{q.updated_question_name}</StyledTitle>
              <StyledFormItem
                key={q.index}
                name={q.index}
                label={<Text strong>Select code frame method</Text>}
                rules={[
                  {
                    required: true,
                    message: 'Selection is required for upload',
                  },
                ]}
              >
                <Select
                  placeholder="Select code frame method"
                  onChange={(value) => onMethodChange(q.index, value)}
                  options={[
                    {
                      value: QuestionProcessingType.REUSE_CODE_FRAME,
                      label: 'Reapply from past Fathom project',
                    },
                    {
                      value: QuestionProcessingType.SKIP,
                      label: isProProcessing ? 'Enter your own' : 'Provide your own',
                    },
                    {
                      value: QuestionProcessingType.AI_GENERATED,
                      label: isProProcessing
                        ? 'Start with suggested themes'
                        : 'Create from scratch to meet your goals',
                    },
                  ]}
                />
              </StyledFormItem>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues[q.index] !== currentValues[q.index]
                }
              >
                {({ getFieldValue }) => {
                  if (getFieldValue(q.index) === 'reuse_code_frame') {
                    return (
                      <StyledFormItem
                        name={`code_frame${q.index}`}
                        rules={[
                          {
                            validator: (_, val) => {
                              if (!val?.surveyId || !val?.codeFrameId) {
                                return Promise.reject(
                                  new Error('Please select both a survey and a question'),
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <CodeFrameSelect
                          questionIndex={q.index}
                          onQuestionSelect={onQuestionSelect}
                          label={
                            <Text strong>
                              Select the survey and question to reapply code frame from
                            </Text>
                          }
                        />
                      </StyledFormItem>
                    );
                  }
                  if (getFieldValue(q.index) === 'skip' && !isProProcessing) {
                    const subject = `Code frame for ${q.updated_question_name}`;
                    const body = [
                      'Hi Fathom,',
                      `Attached is the code frame for the question “${q.updated_question_name}” included in the recent “${surveyData.surveyDetails.survey_title}” upload.`,
                      'Thanks!',
                    ].join('\n\n');
                    const mailtoUrl = `mailto:support@fathomthat.ai?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                    return (
                      <Text>
                        Send your code frame to <a href={mailtoUrl}>support@fathomthat.ai</a> and
                        we&apos;ll take care of the rest.
                      </Text>
                    );
                  }
                  return null;
                }}
              </Form.Item>
            </Flex>
          </Card>
        ))}
        <Form.Item>
          <Flex justify="end">
            <Space>
              <Button onClick={prev} disabled={createSurveyLoading}>
                Back
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createSurveyLoading}
                disabled={createSurveyLoading}
                data-cy="finish-upload"
              >
                Finish upload
              </Button>
            </Space>
          </Flex>
        </Form.Item>
      </Flex>
    </Form>
  );
}

CodingGuidance.propTypes = {
  surveyData: surveyDetailsType.isRequired,
  prev: PropTypes.func.isRequired,
  createSurveyLoading: PropTypes.bool.isRequired,
  finishUpload: PropTypes.func.isRequired,
  setSurveyData: PropTypes.func.isRequired,
};

export default CodingGuidance;
